import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '../services/firebase';
import { doc, getDoc } from "firebase/firestore";
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  getIdTokenResult
} from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userClaims, setUserClaims] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Enhanced login function
  const login = async (email, password) => {
    try {
      console.log('🔐 Attempting login for:', email);
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      console.log('✅ Firebase auth successful, fetching user profile...');

      // 🔍 Check user profile in Firestore
      let userData = null;
      
      // Try agents collection first
      const agentRef = doc(db, "agents", user.uid);
      const agentSnap = await getDoc(agentRef);

      if (agentSnap.exists()) {
        userData = agentSnap.data();
        console.log('✅ User found in agents collection');
      } else {
        // Try users collection as fallback
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          userData = userSnap.data();
          console.log('✅ User found in users collection');
        }
      }

      // If no profile found but it's the super admin email
      if (!userData && email === 'awais.bodla@bodlabuilders.com.pk') {
        userData = {
          uid: user.uid,
          email: user.email,
          firstName: 'Super',
          lastName: 'Admin',
          role: 'admin',
          isSuperAdmin: true,
          createdAt: new Date()
        };
        console.log('✅ Super admin profile created');
      }

      if (!userData) {
        console.warn('❌ User profile not found in Firestore');
        await signOut(auth);
        throw new Error("Account not found. Please contact administrator.");
      }

      // Check if user is blocked
      if (userData.blocked === true || userData.status === "blocked") {
        console.warn("🚫 Blocked user attempted login:", email);
        await signOut(auth);
        throw new Error("Your account has been blocked. Please contact admin.");
      }

      setUserProfile(userData);

      // Refresh token and get claims
      await user.getIdToken(true);
      const idTokenResult = await getIdTokenResult(user);
      setUserClaims(idTokenResult.claims);

      console.log('✅ Login completed successfully for:', email);
      return userCredential;

    } catch (error) {
      console.error('❌ Login error:', error);
      
      // Provide user-friendly error messages
      let errorMessage = error.message;
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      }
      
      throw new Error(errorMessage);
    }
  };

  const logout = () => {
    setUserProfile(null);
    setUserClaims(null);
    return signOut(auth);
  };

  // Enhanced role checking
  const isAdmin = () => {
    // Super admin email check
    if (currentUser?.email === 'awais.bodla@bodlabuilders.com.pk') {
      return true;
    }
    
    // Check user profile role
    if (userProfile?.role === 'admin') {
      return true;
    }
    
    // Check Firebase claims
    return userClaims?.admin === true;
  };

  const getUserRole = () => {
    if (currentUser?.email === 'awais.bodla@bodlabuilders.com.pk') {
      return 'admin';
    }
    return userProfile?.role || 'agent';
  };

  const getUserProfile = () => {
    return userProfile;
  };

  // ✅ Enhanced auth state change handler
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          console.log("🔄 Auth state changed - User detected:", user.email);

          let userData = null;
          
          // Try to get user profile from Firestore
          try {
            const agentRef = doc(db, "agents", user.uid);
            const agentSnap = await getDoc(agentRef);

            if (agentSnap.exists()) {
              userData = agentSnap.data();
            } else {
              const userRef = doc(db, "users", user.uid);
              const userSnap = await getDoc(userRef);
              
              if (userSnap.exists()) {
                userData = userSnap.data();
              }
            }

            // Super admin fallback
            if (!userData && user.email === 'awais.bodla@bodlabuilders.com.pk') {
              userData = {
                uid: user.uid,
                email: user.email,
                firstName: 'Super',
                lastName: 'Admin',
                role: 'admin',
                isSuperAdmin: true,
                createdAt: new Date()
              };
            }

          } catch (firestoreError) {
            console.error('Error fetching user profile:', firestoreError);
          }

          if (userData) {
            // Check if user is blocked
            if (userData.blocked === true || userData.status === "blocked") {
              console.warn("🚫 Blocked user detected, signing out:", user.email);
              await signOut(auth);
              setCurrentUser(null);
              setUserProfile(null);
              setUserClaims(null);
              setLoading(false);
              return;
            }

            setUserProfile(userData);
          }

          // Get claims
          const idTokenResult = await getIdTokenResult(user);
          setUserClaims(idTokenResult.claims);
          setCurrentUser(user);

          console.log('✅ Auth state updated successfully');

        } catch (error) {
          console.error("❌ Error in auth state change:", error);
          setCurrentUser(null);
          setUserProfile(null);
          setUserClaims(null);
        }
      } else {
        console.log('🔒 No user signed in');
        setCurrentUser(null);
        setUserProfile(null);
        setUserClaims(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    userClaims,
    login,
    logout,
    isAdmin,
    getUserRole,
    getUserProfile,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};